/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-lines */
import { Box } from '@chakra-ui/react';
import { useNavigationWithQueryParams } from '@melio/ap-domain';
import { Button, Container, Group, Table, useBreakpoint, useDisclosure, useTable } from '@melio/penny';
import { useAnalytics, withAnalyticsContext } from '@melio/platform-analytics';
import { Vendor } from '@melio/platform-api';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { useMelioIntl } from '@melio/platform-i18n';
import { useMonitoring } from '@melio/platform-monitoring';
import { useConfig } from '@melio/platform-provider';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useMonitorTiming } from '../../../../utils';
import { useRecommendedVendors } from '../../../recommended-vendors';
import { useLoadingState } from '../../hooks/useLoadingState';
import { useSearchTerm } from '../../hooks/useSearchTerm';
import { useSkipToLink } from '../../hooks/useSkipToLink';
import { PayDashboardSortingProvider, usePayDashboardSorting } from '../../PayDashboardSortingProvider';
import { MobileSortMenu } from '../MobileSortMenu/MobileSortMenu';
import { PayDashboardPagination, PayDashboardPaginationProvider, usePayDashboardPagination } from '../Pagination';
import { EmptySearchResult, SearchBar } from '../Search';
import { DeleteVendorModal } from './components/DeleteVendor/DeleteVendorModal';
import { VendorsBatchPaymentsModalActivity } from './components/VendorsBatchPaymentsModalActivity/VendorsBatchPaymentsModal-activity';
import { VendorsSelectionFooter } from './components/VendorsSelectionFooter';
import { VendorsTabEmptyState } from './components/VendorsTabEmptyState';
import { VendorsTabListItem } from './components/VendorsTabListItem';
import { VENDORS_SELECTION_FOOTER_REVIEW_AND_PAY_BUTTON_ID } from './consts';
import { usePayVendor } from './hooks/usePayVendor';
import { CELLS_IDS, VendorsTableMeta } from './types';
import { useVendorsTabAnalytics } from './useVendorsTabAnalytics';
import { useVendorsTabData } from './useVendorsTabData';
import { useVendorsTableColumns } from './useVendorsTableColumns';
import { useVendorsTabSelection } from './useVendorsTabSelection';
import { useVendorsTabSortableColumns } from './useVendorsTabSortableColumns';
import { useAriaLabelContextForVendor } from './utils';

export type VendorsTabProps = {
  onAddNewVendor: ({ returnUrl }: { returnUrl: string }) => void;
  onAddInstantBill: ({ vendorId, amount }?: { vendorId?: Vendor['id']; amount?: string }) => void;
  onAddNewBill: ({ returnUrl, vendorId }: { returnUrl: string; vendorId?: Vendor['id']; amount?: string }) => void;
  onEBillSubscribeClick: ({ vendorId }: { vendorId: string }) => void;
  onAutoPayActivationClick: ({ vendorId }: { vendorId: string }) => void;
};

export const VendorsTabComponent = withAnalyticsContext(
  ({
    onAddNewVendor,
    onAddInstantBill,
    onAddNewBill,
    onEBillSubscribeClick,
    onAutoPayActivationClick,
  }: VendorsTabProps) => {
    const { searchTerm } = useSearchTerm();
    const { isExtraSmallScreen } = useBreakpoint();
    const { getAriaLabelContextForVendor } = useAriaLabelContextForVendor();
    const {
      settings: { isLineItemsEnabled: lineItemsEnabled },
    } = useConfig();
    const [isLineItemsCombineViewEnabled] = useFeature(FeatureFlags.LineItemsCombineView, false);
    const [isMobileSortEnabled] = useFeature<boolean>(FeatureFlags.NpeSortMobile, false);
    const [isVendorsBatchPaymentsEnabled] = useFeature<boolean>(
      FeatureFlags.PlatformVendorsBatchNewPaymentsEnabled,
      false
    );
    const { sortableColumns: vendorsTabSortableItems } = useVendorsTabSortableColumns();
    const sorting = usePayDashboardSorting();
    const { resetToFirstPage } = usePayDashboardPagination();
    const { unpaidBills, ...paginationResponse } = useVendorsTabData({ searchTerm });
    const { data: vendorsResult, isLoading: isLoadingVendors, isFetching, isPreviousData } = paginationResponse;
    const { data: vendors = [], pagination } = vendorsResult ?? {};
    const { isLoading: isRecommendedVendorsLoading } = useRecommendedVendors({ shouldFetch: true });
    const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
    const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
    const {
      isOpen: isVendorsBatchPaymentsAmountsModalOpen,
      onOpen: onVendorsBatchPaymentsAmountsModalOpen,
      onClose: onVendorsBatchPaymentsAmountsModalClose,
    } = useDisclosure();
    const { pathname } = useLocation();
    const { formatMessage } = useMelioIntl();
    const { handleNavigationWithQueryParams } = useNavigationWithQueryParams();
    const legacyRouteReady = useMonitorTiming('vendors_tab_ready');
    const { routeReady } = useMonitoring();
    const triggerMonitoring = (el: HTMLDivElement) => {
      legacyRouteReady(el);
      routeReady(el);
    };
    const { track } = useAnalytics();
    const {
      isEmptyState,
      isEmptySearchResult,
      isInitialLoading,
      isTableLoading,
      shouldShowPaginationControls,
      shouldShowTabHeader,
    } = useLoadingState({
      isLoading: isLoadingVendors || (vendors.length > 0 ? false : isRecommendedVendorsLoading),
      searchTerm,
      items: vendors,
      paginationConfig: {
        isFetching,
        isPreviousData,
        totalCount: pagination?.totalCount,
      },
    });

    useEffect(() => {
      if (isTableLoading) {
        return;
      }
      track('Dashboard', 'View', {
        Intent: 'view-vendors',
        IsSuggestedVendorsShown: null,
        isEmptyState,
      });
    }, [isTableLoading]);

    const handleNewPaymentClick = (vendorId: string, amount?: string) => {
      onAddInstantBill({ vendorId, amount });
    };

    const handleAddNewBill = (vendorId: Vendor['id']) => {
      if (isLineItemsCombineViewEnabled || !lineItemsEnabled) {
        return onAddNewBill({ returnUrl: pathname, vendorId });
      }
      return onAddInstantBill({ vendorId });
    };

    const handleDeleteVendorClick = (vendor: Vendor) => {
      setSelectedVendor(vendor);
      onDeleteModalOpen();
    };

    const handleEditVendor = (vendorId: Vendor['id']) => {
      handleNavigationWithQueryParams({ newPath: `vendor/${vendorId}`, newUrlQueryParam: { edit: 'true' } });
    };

    const handleViewVendor = (vendorId: Vendor['id']) => {
      handleNavigationWithQueryParams({ newPath: `vendor/${vendorId}` });
    };

    const handleSelectionFooterReviewAndPay = () => {
      onVendorsBatchPaymentsAmountsModalOpen();
    };

    const columns = useVendorsTableColumns({ onSortChange: resetToFirstPage });
    const mobileSortMenuOptions = Object.values(vendorsTabSortableItems);

    const onClickRow = (rowData: Vendor) => {
      track('Dashboard', 'Click', {
        Intent: 'view-vendor',
        Cta: 'vendor-details',
        VendorId: rowData.id,
      });
      handleNavigationWithQueryParams({ newPath: `vendor/${rowData.id}` });
    };

    const mobileRowRenderer = useCallback(
      (row: Vendor, meta?: VendorsTableMeta) => (
        <VendorsTabListItem
          vendor={row}
          unpaidBills={meta?.unpaidBills.filter((bill) => bill.vendorId === row.id) ?? []}
        />
      ),
      []
    );

    const {
      rowSelections,
      selectedRows,
      rowSelectionTooltips,
      onAllRowsSelectionChange,
      onRowSelectionChange,
      areAllSelected,
    } = useVendorsTabSelection(vendors);
    const { amounts, onAmountChange } = usePayVendor({ onRowSelectionChange });

    useVendorsTabAnalytics({ vendors, unpaidBills, areAllSelected });

    const tableProps = useTable({
      isHeaderSticky: true,
      isLoading: isTableLoading,
      data: vendors,
      columns,
      getRowId: (row) => row.id,
      onRowClick: ({ rowData }) => onClickRow(rowData),
      rowSelectionTooltips,
      selectedRows,
      onRowSelectionChange,
      onAllRowsSelectionChange,
      ...sorting,
      headerVariant: 'dark',
      mobileRowRenderer,
      getRowSelectionAriaLabel: useCallback(
        (vendor) => getAriaLabelContextForVendor(vendor, { unpaidBills }),
        [unpaidBills]
      ),
      getRowAriaLabel: useCallback(
        ({ rowData }) => getAriaLabelContextForVendor(rowData, { unpaidBills }),
        [unpaidBills]
      ),
      hideHeaderWhileLoading: isInitialLoading,
      allRowsSelectionAriaLabel: formatMessage('activities.payDashboard.vendorsTab.table.selectAllRows'),
      captionId: formatMessage(`activities.payDashboard.tabs.vendors.caption`),
      meta: {
        unpaidBills,
        onNewPaymentClick: handleNewPaymentClick,
        onDeleteVendorClick: handleDeleteVendorClick,
        onAddNewBillClick: handleAddNewBill,
        onEditVendorClick: handleEditVendor,
        onViewVendorClick: handleViewVendor,
        onEBillSubscribeClick: (vendorId) => onEBillSubscribeClick({ vendorId }),
        onAutoPayActivationClick: (vendorId) => onAutoPayActivationClick({ vendorId }),
        onAmountChange,
        amounts,
        getItemAriaLabelContext: (vendor) => getAriaLabelContextForVendor(vendor, { unpaidBills }),
      },
      skipToLink: useSkipToLink(
        VENDORS_SELECTION_FOOTER_REVIEW_AND_PAY_BUTTON_ID,
        formatMessage('activities.payDashboard.vendorsTab.selectionFooter.reviewAndPay.skipTo')
      ),
    });

    const handleVendorNewVendor = () => {
      track('Dashboard', 'Click', {
        Intent: 'add-vendor',
        Cta: 'add-vendor',
      });
      onAddNewVendor({ returnUrl: pathname });
    };
    const handleDeleteModalClose = () => {
      setSelectedVendor(null);
      onDeleteModalClose();
    };
    const renderTableContent = () => (
      <Group alignItems="flex-end" variant="vertical" spacing={shouldShowPaginationControls ? 'm' : undefined}>
        <Table {...tableProps} />
        {isEmptySearchResult && !isFetching ? <EmptySearchResult /> : null}
        <>
          <PayDashboardPagination paginatedCollection={paginationResponse} isVisible={shouldShowPaginationControls} />
          {/* Fills space on the DOM equals to gap required by pagination controls in order for ActionBar footer to respect it due to it's fixed position  */}
          {shouldShowPaginationControls ? <Box height={6} /> : null}
        </>
      </Group>
    );

    const renderContent = () => {
      if (isEmptySearchResult || !isEmptyState) {
        return renderTableContent();
      }
      return <VendorsTabEmptyState onAddNewVendor={handleVendorNewVendor} />;
    };
    return (
      <>
        <Container data-testid="pay-dashboard-vendors-tab" overflow="initial">
          {!isLoadingVendors && <span ref={triggerMonitoring} />}
          <Box paddingBottom="xxl">
            <Group variant="vertical" width="full" spacing="m">
              <Container overflow="initial">
                {shouldShowTabHeader && (
                  <Group justifyContent="space-between" width="full" alignItems="center">
                    <SearchBar
                      onSearchSubmitted={resetToFirstPage}
                      isDisabled={isTableLoading || isEmptyState}
                      placeholderOnFocus="activities.payDashboard.vendorsTab.searchPlaceholder"
                      aria-label={formatMessage('activities.payDashboard.vendorsTab.searchAriaLabel')}
                      pagination={!paginationResponse.isFetching ? paginationResponse.data?.pagination : undefined}
                    />
                    {!isExtraSmallScreen ? (
                      <Button
                        data-testid="vendors-tab-add-vendor-button"
                        onClick={handleVendorNewVendor}
                        label={formatMessage('activities.payDashboard.vendorsTab.addVendorButton')}
                        variant="tertiary"
                        icon="add"
                      />
                    ) : null}
                    {isExtraSmallScreen && isMobileSortEnabled && (
                      <MobileSortMenu items={mobileSortMenuOptions} onSortChange={resetToFirstPage} />
                    )}
                  </Group>
                )}
              </Container>
              <Container overflow="initial">{renderContent()}</Container>
            </Group>
          </Box>
          {selectedVendor && (
            <DeleteVendorModal vendor={selectedVendor} isOpen={isDeleteModalOpen} onClose={handleDeleteModalClose} />
          )}
        </Container>
        {isVendorsBatchPaymentsEnabled && rowSelections.length > 0 && (
          <VendorsSelectionFooter
            vendorIds={rowSelections}
            onReviewAndPay={handleSelectionFooterReviewAndPay}
            vendorsAmountsMap={amounts}
          />
        )}
        {isVendorsBatchPaymentsEnabled && rowSelections.length > 0 && (
          <VendorsBatchPaymentsModalActivity
            selectedVendorIds={rowSelections}
            vendorsAmountsMap={amounts}
            isOpen={isVendorsBatchPaymentsAmountsModalOpen}
            onClose={onVendorsBatchPaymentsAmountsModalClose}
            onAddInvoiceClick={handleNewPaymentClick}
          />
        )}
      </>
    );
  }
);

export const VendorsTab = (props: VendorsTabProps) => (
  <PayDashboardPaginationProvider>
    <PayDashboardSortingProvider defaultSort={{ id: CELLS_IDS.LAST_PAID, sortDirection: 'desc' }}>
      <VendorsTabComponent {...props} />
    </PayDashboardSortingProvider>
  </PayDashboardPaginationProvider>
);
